import { IXmlNode } from '@/app-modules/server-communications';
import { IPrizeStructure, IPrizeStake } from '@/components/game/implementations/_default/_domain/models';
import { CatchUpData, ResetData, CallsData, NextGameData, PrizeData } from '@/components/game/_domain/server/prize/prize-data';
import { StarshipPrizeType, getPrizeTypeName } from '@/components/game/implementations/uk/starship/_domain/models';
import { getGameConfiguration } from '@/components/game/_domain/helpers';
import { ConfigurationPaths } from '@/app-modules/config';

class StarshipPrizeData extends PrizeData {
  constructor(node: IXmlNode) {
    super(node);
  }

  private get stakeValues(): number[] {
    const gameConfiguration = getGameConfiguration();
    const gameConfigurationPaths = this.getGameConfigurationPaths();
    const stakeValues = gameConfiguration.getConfigurationByPath(gameConfigurationPaths.stakevalues);
    return stakeValues.split(',').map((item) => parseInt(item, 10));
  }

  private get prizeTypes(): StarshipPrizeType[] {
    return [
      StarshipPrizeType.Jackpot,
      StarshipPrizeType.FullHouse,
      StarshipPrizeType.Star
    ];
  }

  private getGameConfigurationPaths() {
    return ConfigurationPaths.forGame('starship');
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    return {
      stakes: this.stakeValues.map((stakeValue) => this.getPrizeStakeForStakeValue(stakeValue, node))
    };
  }

  private getPrizeStakeForStakeValue(stakeValue: number, node: IXmlNode): IPrizeStake {
    return {
      stakeValue,
      ticketBooks: [
        {
          id: 'default',
          name: 'Default',
          prizes: this.prizeTypes.map((prizeType) => {
            const isJackpot = prizeType === StarshipPrizeType.Jackpot;

            return {
              name: getPrizeTypeName(prizeType),
              prize: this.getPrizeValueForPrizeTypeAndStakeValue(prizeType, stakeValue, node),
              disablePrize: this.disablePrize(prizeType, node),
              isJackpot,
              jackpotCalls: isJackpot ? this.getJackpotCallsForPrizeType(prizeType, node) : 0
            };
          })
        }
      ]
    };
  }

  private getJackpotCallsForPrizeType(prizeType: StarshipPrizeType, node: IXmlNode): number {
    let jackpotCalls: number[] = [];
    if (node.attributes.jpcalls) {
      jackpotCalls = node.attributes.jpcalls.split(',').map((item: string) => parseInt(item, 10));
    }

    if ((prizeType === StarshipPrizeType.Jackpot) && (jackpotCalls.length > 0)) {
      return jackpotCalls[0];
    }

    return 0;
  }

  private getPrizeValueForPrizeTypeAndStakeValue(prizeType: StarshipPrizeType, stakeValue: number, node: IXmlNode): number {
    let jackpotMultiplier: number[] = [];
    if (node.attributes.jpmultiples) {
      jackpotMultiplier = node.attributes.jpmultiples.split(',').map((item: string) => parseInt(item, 10));
    }

    if (prizeType === StarshipPrizeType.Jackpot) {
      if (jackpotMultiplier.length > 0) {
        return stakeValue * jackpotMultiplier[0];
      } else {
        return 0;
      }
    }

    const prizeStakeType = `prize${stakeValue}`;
    const prizeSplit = node.attributes[prizeStakeType].split(',');
    const prizeIndex = (this.prizeTypes.length - this.prizeTypes.indexOf(prizeType)) - 1; // Reverse order to index into prizeSplit
    return Number(prizeSplit[prizeIndex]);
  }

  private disablePrize(prizeType: StarshipPrizeType, node: IXmlNode): boolean {
    return false;
  }
}

class StarshipCatchUpData extends CatchUpData {
  private prizeData: StarshipPrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new StarshipPrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

class StarshipResetData extends ResetData {
  private prizeData: StarshipPrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new StarshipPrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

class StarshipCallsData extends CallsData {
  constructor(node: IXmlNode) {
    super(node);
  }
}

class StarshipNextGameData extends NextGameData {
  private prizeData: StarshipPrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new StarshipPrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

export {
  StarshipCatchUpData,
  StarshipResetData,
  StarshipCallsData,
  StarshipNextGameData
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/uk/starship/_domain/models/prize.ts